import React, { useState, useRef } from 'react';
import Pagination from '../components/common/Pagination';
import useDeliveryOrders from '../hooks/useDeliveryOrders';
import ConfirmedOrdersActions from '../components/confirmed-orders/ConfirmedOrdersActions';
import ConfirmedOrdersTable from '../components/confirmed-orders/ConfirmedOrdersTable';
import OrdersViewModal from '../components/orders/OrdersViewModal';
import AssignDeliveryPersonnel from '../components/delivery-orders/AssignDeliveryPersonnel';
import MessageAlert from '../components/common/MessageAlert';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useErrorDialog } from '../context/errorDialogContext';
import { UI_COLORS, colorHelpers } from '../utils/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox } from '@fortawesome/free-solid-svg-icons';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'confirmed_orders_page_size';

const ConfirmedOrdersPage = () => {
  const { t } = useTranslation();
  const {
    orders,
    loading,
    error,
    fetchOrders,
    total: totalCount,
    assignSupervisor,
    fetchOrderById,
  } = useDeliveryOrders();

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [showAssignPersonnelModal, setShowAssignPersonnelModal] = useState(false);
  const [assignPersonnelOrderId, setAssignPersonnelOrderId] = useState(null);
  const [assignPersonnelInitialValues, setAssignPersonnelInitialValues] = useState(null);

  const [viewingOrder, setViewingOrder] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
  });
  const [committedSearch, setCommittedSearch] = useState('');
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const searchDebounceRef = useRef(null);

  const strings = t('orders', { returnObjects: true });
  const { showError } = useErrorDialog();

  // Debounce search → committedSearch
  React.useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm]);

  // Fetch confirmed orders when page or committed search changes
  React.useEffect(() => {
    const skip = (currentPage - 1) * pageSize;
    const params = { skip, limit: pageSize, status: 'pending' };
    if (committedSearch) params.search = committedSearch;
    void fetchOrders(params, { force: true });
  }, [currentPage, committedSearch, fetchOrders, pageSize]);

  React.useEffect(() => {
    if (error) showError(error, strings.page?.alerts?.errorTitle);
  }, [error, showError, strings.page]);

  const selectOrder = (id, checked) => {
    setSelectedOrders(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };
  const selectAllOrders = (checked) => {
    if (checked) setSelectedOrders(orders.map(i => i.id));
    else setSelectedOrders([]);
  };

  const hasSelection = selectedOrders.length > 0;
  const selectionCount = selectedOrders.length;

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      const skip = (currentPage - 1) * pageSize;
      const params = { skip, limit: pageSize, status: 'pending' };
      if (committedSearch) params.search = committedSearch;
      await fetchOrders(params, { force: true });
    } catch (err) {
      console.error('Failed to refresh confirmed orders', err);
    } finally {
      setRefreshLoading(false);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
  };

  const handleAssignPersonnel = (orderId, order) => {
    setAssignPersonnelOrderId(orderId);
    setAssignPersonnelInitialValues(order || null);
    setShowAssignPersonnelModal(true);
  };

  const handleAssignPersonnelSubmit = async (orderId, payload) => {
    setActionLoading(true);
    try {
      const result = await assignSupervisor(orderId, payload);
      if (result.success) {
        setShowAssignPersonnelModal(false);
        setAssignPersonnelOrderId(null);
        setAssignPersonnelInitialValues(null);
        // Show the success dialog using ConfirmDialog instead of the generic alert
        setShowSuccessDialog(true);
        try { await handleRefresh(); } catch (e) { console.error('Failed to refresh after assign', e); }
      } else {
        setAlertMessage({ type: 'error', title: t('common.status.failed'), message: result.error || 'Failed to assign supervisor' });
      }
      return result;
    } catch (err) {
      console.error('Assign personnel failed', err);
      const errResult = { success: false, error: err.message || 'Failed to assign supervisor' };
      setAlertMessage({ type: 'error', title: t('common.status.failed'), message: errResult.error });
      return errResult;
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    setViewLoading(true);
    setViewingOrder(null);
    setShowViewModal(true);
    try {
      // Use deliveryOrderService for fetching sub-order details
      const response = await fetchOrderById(orderId);
      if (response.success && response.data) setViewingOrder(response.data);
      else showError(response.error || t('orders.alerts.fetchFailed'), strings.page?.alerts?.errorTitle || t('orders.page.alerts.errorTitle'));
    } catch (err) {
      console.error('View order failed', err);
      showError(err.message || t('orders.alerts.fetchFailed'), strings.page?.alerts?.errorTitle || t('orders.page.alerts.errorTitle'));
    } finally {
      setViewLoading(false);
    }
  };


  const totalOrdersCount = totalCount;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {alertMessage && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert type={alertMessage.type} title={alertMessage.title} message={alertMessage.message} onClose={() => setAlertMessage(null)} />
        </div>
      )}

      {actionLoading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.7) }}
        >
          <div className="flex flex-col items-center">
            <div
              className="h-16 w-16 animate-spin rounded-full border-4 border-b-transparent"
              style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.7) }}
            ></div>
            <p className="mt-4 text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              Loading...
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY || '#05445E' }}>
            Confirmed Orders
          </h1>
          <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY || '#189AB4' }}>
            Manage confirmed orders and assign delivery supervisors
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div
            className="flex items-center gap-3 rounded-2xl border px-4 py-3"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.15),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              <FontAwesomeIcon icon={faBox} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {strings.page?.stats?.total}
              </p>
              <p className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {totalOrdersCount}
              </p>
            </div>
          </div>
          {hasSelection && (
            <div
              className="rounded-2xl border px-4 py-3"
              style={{
                borderColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.4),
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
              }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.STATUS.INFO }}>
                {strings.page?.stats?.selected}
              </p>
              <p className="text-xl font-bold" style={{ color: UI_COLORS.STATUS.INFO }}>
                {selectionCount}
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmedOrdersActions
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={handleRefresh}
        loading={actionLoading}
        refreshLoading={refreshLoading}
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <ConfirmedOrdersTable
          orders={orders}
          loading={loading}
          selectedOrders={selectedOrders}
          onSelectOrder={selectOrder}
          onSelectAll={selectAllOrders}
          onViewOrder={handleViewOrder}
          onAssignSupervisor={handleAssignPersonnel}
          viewLoading={viewLoading}
          onRefresh={handleRefresh}
        />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        loading={loading}
        totalLabel="total orders"
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {showViewModal && (
        <OrdersViewModal
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingOrder(null); }}
          order={viewingOrder}
          loading={viewLoading}
        />
      )}

      {showAssignPersonnelModal && (
        <AssignDeliveryPersonnel
          isOpen={showAssignPersonnelModal}
          orderId={assignPersonnelOrderId}
          initialValues={assignPersonnelInitialValues}
          onClose={() => {
            setShowAssignPersonnelModal(false);
            setAssignPersonnelOrderId(null);
            setAssignPersonnelInitialValues(null);
          }}
          onSubmit={handleAssignPersonnelSubmit}
        />
      )}

      {showSuccessDialog && (
        <ConfirmDialog
          isOpen={showSuccessDialog}
          title="Supervisor Assigned"
          message="The delivery supervisor has been successfully assigned. The order has now moved to the Delivery Orders page."
          confirmLabel="Got it"
          cancelLabel={null}
          confirmVariant="success"
          onConfirm={() => setShowSuccessDialog(false)}
          onCancel={() => setShowSuccessDialog(false)}
        />
      )}

    </div>
  );
};

export default ConfirmedOrdersPage;
